import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Maximize2, Loader2, ArrowLeft, MessageCircle } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { apiGet } from '../../utils/api';
import { useTeamChat } from '../../hooks/useTeamChat';
import ConversationList from './ConversationList';
import ConversationView from './ConversationView';

export default function TeamChatDrawer({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  const isDark = theme === 'dark';

  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [latestEvent, setLatestEvent] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'conversation'

  // WebSocket event handler (mirrors TeamChatPage)
  const handleEvent = useCallback((event) => {
    setLatestEvent(event);

    if (event.type === 'new_message') {
      setConversations(prev => {
        const updated = prev.map(c => {
          if (c.id === event.conversation_id) {
            return {
              ...c,
              last_message: {
                content: event.message.content,
                user: event.message.user,
                created_at: event.message.created_at
              },
              last_message_at: event.message.created_at,
              unread_count: (c.unread_count || 0) + 1
            };
          }
          return c;
        });
        return updated.sort((a, b) =>
          new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0)
        );
      });
    }

    if (event.type === 'conversation_updated') {
      setConversations(prev => {
        const exists = prev.find(c => c.id === event.conversation?.id);
        if (exists) {
          return prev.map(c => c.id === event.conversation.id ? { ...c, ...event.conversation } : c);
        }
        return [event.conversation, ...prev];
      });
    }

    if (event.type === 'member_removed' && event.user_id === currentUser?.id) {
      setConversations(prev => prev.filter(c => c.id !== event.conversation_id));
      setActiveConversation(prev => {
        if (prev?.id === event.conversation_id) {
          setView('list');
          return null;
        }
        return prev;
      });
    }
  }, [currentUser?.id]);

  const { isConnected, sendTyping, sendStoppedTyping, markRead } = useTeamChat(handleEvent);

  // Fetch conversations when drawer opens
  useEffect(() => {
    if (!isOpen) return;
    const fetchConversations = async () => {
      setLoading(true);
      try {
        const response = await apiGet('/conversations');
        if (response.ok) {
          const data = await response.json();
          setConversations(data.conversations || []);
        }
      } catch (err) {
        console.error('Failed to fetch conversations:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchConversations();
  }, [isOpen]);

  const handleSelectConversation = (conversation) => {
    setActiveConversation(conversation);
    setView('conversation');
    setConversations(prev =>
      prev.map(c => c.id === conversation.id ? { ...c, unread_count: 0 } : c)
    );
    markRead?.(conversation.id);
  };

  const handleConversationUpdated = (updatedConversation) => {
    setConversations(prev =>
      prev.map(c => c.id === updatedConversation.id ? { ...c, ...updatedConversation } : c)
    );
    if (activeConversation?.id === updatedConversation.id) {
      setActiveConversation(prev => ({ ...prev, ...updatedConversation }));
    }
  };

  const handleBack = () => {
    setView('list');
    setActiveConversation(null);
  };

  const handleExpand = () => {
    onClose();
    navigate('/dashboard/team-chat');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />

      {/* Drawer panel */}
      <div className={`fixed inset-y-0 right-0 z-40 w-full max-w-lg flex flex-col shadow-xl ${
        isDark ? 'bg-gray-900' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b shrink-0 ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-2 min-w-0">
            {view === 'conversation' && (
              <button
                onClick={handleBack}
                className={`p-1 rounded-lg ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <MessageCircle size={18} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
            <h3 className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {view === 'conversation' && activeConversation
                ? activeConversation.name || 'Conversation'
                : 'Team Chat'}
            </h3>
            {isConnected && (
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" title="Connected" />
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleExpand}
              title="Open full page"
              className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            >
              <Maximize2 size={16} />
            </button>
            <button
              onClick={onClose}
              className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading ? (
            <div className="flex items-center justify-center flex-1">
              <Loader2 className="animate-spin text-blue-500" size={24} />
            </div>
          ) : view === 'list' ? (
            <ConversationList
              conversations={conversations}
              activeConversationId={activeConversation?.id}
              onSelectConversation={handleSelectConversation}
              onConversationsUpdated={setConversations}
            />
          ) : (
            <ConversationView
              conversation={activeConversation}
              onConversationUpdated={handleConversationUpdated}
              onBack={handleBack}
              teamChatEvents={latestEvent}
              sendTyping={sendTyping}
              sendStoppedTyping={sendStoppedTyping}
              markRead={markRead}
              sidebarOpen={false}
              onToggleSidebar={() => {}}
            />
          )}
        </div>
      </div>
    </>
  );
}
